import { ClockIcon } from "@heroicons/react/20/solid";
import { ago, TimeEntryId, UserId } from "@sp-crm/core";
import { AuthorName } from "components/byline/author-name";
import { TimeEntryEditForm } from "components/clients/show-client/time-entry-edit-form";
import { formatDuration } from "components/clients/show-client/time-entry-utils";
import { trpcClient } from "components/rpc/trpc";
import { DeleteButton, EditButton } from "components/ui/action-button";
import { fancyAlert } from "components/ui/fancy-alert";
import { Spinner } from "components/ui/spinner";
import React, { useCallback } from "react";
import { breakNewlines } from "util/text";

export interface FeedTimeEntry {
    id: TimeEntryId;
    userId: UserId;
    startTime: string;
    durationMinutes: number;
    note: string | null;
}

interface Props {
    entry: FeedTimeEntry;
    onSaved: () => void;
    onDeleted: (entryId: TimeEntryId) => void;
}

export const ActivityFeedTimeEntry: React.FC<Props> = props => {
    const { entry, onSaved, onDeleted } = props;
    const [isEditing, setIsEditing] = React.useState(false);
    const [isDeleting, setIsDeleting] = React.useState(false);

    const enableEdit = useCallback(() => setIsEditing(true), []);
    const disableEdit = useCallback(() => setIsEditing(false), []);

    const handleSaved = useCallback(() => {
        setIsEditing(false);
        onSaved();
    }, [onSaved]);

    const deleteCallback = useCallback(async () => {
        setIsDeleting(true);
        try {
            await trpcClient.timeEntries.delete.mutate({ id: entry.id });
            onDeleted(entry.id);
        } catch {
            await fancyAlert("Error", "Failed to delete time entry. Please try again.");
        } finally {
            setIsDeleting(false);
        }
    }, [entry.id, onDeleted]);

    return (
        <div className="relative flex items-start space-x-3">
            <div>
                <div className="relative px-1">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 ring-8 ring-white">
                        <ClockIcon
                            className="h-5 w-5 text-brand-600"
                            aria-hidden="true"
                        />
                    </div>
                </div>
            </div>
            <div className="min-w-0 flex-1 group">
                {isEditing ? (
                    <TimeEntryEditForm
                        entryId={entry.id}
                        initialDurationMinutes={entry.durationMinutes}
                        initialNote={entry.note}
                        initialStartTime={entry.startTime}
                        onSaved={handleSaved}
                        onCancel={disableEdit}
                    />
                ) : (
                    <div className="space-y-2">
                        <div>
                            <div className="text-sm">
                                <span className="font-medium text-gray-900">
                                    <AuthorName authorId={entry.userId} />
                                </span>
                            </div>
                            <p className="mt-0.5 flex items-center gap-2 text-sm text-gray-500">
                                <span className="inline-flex items-center rounded bg-brand-50 px-2 py-0.5 text-sm font-semibold text-brand-700 tabular-nums">
                                    {formatDuration(entry.durationMinutes)}
                                </span>
                                <span>{ago(entry.startTime, "")}</span>
                            </p>
                        </div>
                        <div className="text-base text-gray-900">
                            {breakNewlines(entry.note ?? "")}
                        </div>
                        {isDeleting ? (
                            <div className="flex space-x-2 items-center">
                                <span className="text-sm italic text-gray-600">
                                    Deleting...
                                </span>
                                <Spinner />
                            </div>
                        ) : (
                            <div className="flex space-x-2 text-gray-300 group-hover:text-gray-700">
                                <EditButton
                                    backgroundColor="bg-gray-200"
                                    onClick={enableEdit}
                                />
                                <DeleteButton
                                    confirm={{
                                        title: "Delete time entry?",
                                        message:
                                            "Are you sure you want to delete this time entry?",
                                    }}
                                    backgroundColor="bg-gray-200"
                                    onClick={deleteCallback}
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
